import { useCallback, useMemo } from "react";

import { Close as CloseIcon, InfoOutlined as InfoIcon, OpenInNew } from "@mui/icons-material";
import { Box, Button, Divider, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";

import { reportTxt } from "../../../../../../assets/texts/CloudAnalytics";
import { useCustomerId } from "../../../../../../Components/hooks/useCustomerId";
import { useAttributionsContext } from "../../../../../../Context/AttributionsContext";
import { type AttributionWRef, type MetadataOption } from "../../../../../../types";
import { getLetterForIndex } from "../../../../../../utils/string";
import useSegmentTrackEvent from "../../../../../../utils/useSegmentTrackEvent";
import { RuleDivider } from "../../../../allocations/components/RuleDivider";
import { AllocationPathType, getAllocationPath } from "../../../../allocations/utils";
import { DRAWER_TYPES } from "../../constants";
import { useDrawerStore } from "../../hooks/useDrawerStore";
import AllocationLogic from "./components/AllocationLogic";
import Filters from "./components/Filters";

const isAllocationData = (data: unknown, type: string | null): data is MetadataOption =>
  type === DRAWER_TYPES.ALLOCATION && data !== null && typeof data === "object" && "data" in data;

const AllocationViewCard = () => {
  const customerId = useCustomerId();
  const { attributions: allocations } = useAttributionsContext();
  const { data, type, closeDrawer } = useDrawerStore();
  const allocationData = isAllocationData(data, type) ? data : null;
  const { trackEvent } = useSegmentTrackEvent();

  const allocationValues = useMemo((): AttributionWRef[] => {
    if (!allocationData?.data?.values?.length || !allocations.length) return [];
    return allocationData?.data?.values
      .map((value: string) => allocations.find((allocation: AttributionWRef) => allocation.ref.id === value))
      .filter((allocation): allocation is AttributionWRef => allocation !== undefined);
  }, [allocationData, allocations]);

  const handleEditAllocation = useCallback(() => {
    trackEvent("View Allocation", {
      source: "Report Quick View",
      feature: "Cloud Analytics Reports",
    });
    if (allocationData?.data) {
      const allocationId = allocationData.data.id.split(":")[1];
      if (allocationId) {
        window.open(getAllocationPath(customerId, AllocationPathType.VIEW, allocationId), "_blank");
      }
    }
  }, [trackEvent, allocationData?.data, customerId]);

  const handleClose = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          {reportTxt.QUICK_VIEW}: {allocationData?.data.label}
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Stack sx={{ p: 2 }} gap={2}>
        <Button variant="outlined" startIcon={<OpenInNew />} fullWidth color="primary" onClick={handleEditAllocation}>
          {reportTxt.QUICK_VIEW_EDIT_ALLOCATION}
        </Button>
        <Box>
          {allocationValues.map(({ data, ref }, index) => {
            const isFirst = index === 0;
            const variablesArray = data?.filters?.map((_, index) => getLetterForIndex(index)) || [];
            return (
              <Stack key={ref.id}>
                {!isFirst && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={isFirst ? "flex-end" : "space-between"}
                    sx={{ position: "relative", height: "78px" }}
                  >
                    <Stack>
                      <RuleDivider />
                      <Box sx={{ ml: 7 }}>
                        <Typography variant="body1">{reportTxt.QUICK_VIEW_ALLOCATION_RULE_DIVIDER}</Typography>
                      </Box>
                    </Stack>
                  </Stack>
                )}
                <Paper
                  elevation={0}
                  sx={({ palette }) => ({
                    backgroundColor: palette.background.default,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: palette.divider,
                    p: 1.5,
                  })}
                >
                  <Stack gap={1}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {data.name}
                    </Typography>
                    <Filters filters={data?.filters || []} />
                    <Divider />
                    <AllocationLogic formula={data.formula} tokens={variablesArray} />
                  </Stack>
                </Paper>
              </Stack>
            );
          })}
          {allocationData?.data?.nullFallback && (
            <Stack direction="row" alignItems="center" sx={{ width: "100%", position: "relative" }}>
              <RuleDivider isLast height="30px" />

              <Stack sx={{ ml: 8, mt: 4.25 }} direction="row" alignItems="center" gap={1}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {allocationData?.data?.nullFallback}
                </Typography>
                <Tooltip title={reportTxt.QUICK_VIEW_UNALLOCATED} arrow placement="right">
                  <InfoIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                </Tooltip>
              </Stack>
            </Stack>
          )}
        </Box>
      </Stack>
    </>
  );
};

export default AllocationViewCard;
